import { useMemo } from "react"
import { WebhookCapabilityConfigSchema } from "schema"
import z from "zod"

type TypeField = z.infer<typeof WebhookCapabilityConfigSchema>["dataToExtract"][number]["type"]

type ExtractionField = {
  name: string
  type: TypeField
  description?: string
  required?: boolean
}

type VariableOption = {
  value: string
  label: string
}

const CUSTOMER_FIELDS: VariableOption[] = [
  { value: "customer.name", label: "customer.name" },
  { value: "customer.firstName", label: "customer.firstName" },
  { value: "customer.lastName", label: "customer.lastName" },
  { value: "customer.email", label: "customer.email" },
  { value: "customer.phone", label: "customer.phone" },
  { value: "customer.whatsappNumber", label: "customer.whatsappNumber" },
  { value: "customer.tentativeName", label: "customer.tentativeName" },
  { value: "customer.tentativeEmail", label: "customer.tentativeEmail" },
  { value: "customer.tentativePhone", label: "customer.tentativePhone" },
  { value: "customer.country", label: "customer.country" },
  { value: "customer.locale", label: "customer.locale" },
  { value: "customer.preferredLocale", label: "customer.preferredLocale" },
  { value: "customer.externalId", label: "customer.externalId" },
  { value: "customer.metadata", label: "customer.metadata" },
]

export function useCapabilityVariables(
  extractionSchema: ExtractionField[] | undefined,
  testResponseBody?: string | null,
  stepTestResponses?: Array<string | null | undefined>,
): VariableOption[] {
  return useMemo(() => {
    const variables: VariableOption[] = [...CUSTOMER_FIELDS]
    const seen = new Set(variables.map((variable) => variable.value))

    const addVariable = (value: string) => {
      if (seen.has(value)) return
      seen.add(value)
      variables.push({ value, label: value })
    }

    if (extractionSchema) {
      for (const field of extractionSchema) {
        if (field.name) {
          addVariable(`extracted.${field.name}`)
        }
      }
    }

    if (stepTestResponses?.length) {
      for (const [index, body] of stepTestResponses.entries()) {
        if (!body) continue

        try {
          const parsed = JSON.parse(body)
          extractVariablesFromObject(parsed, `steps[${index}]`, addVariable)
        } catch (error) {
          console.warn(`Failed to parse step ${index} test response body as JSON:`, error)
        }
      }

      const lastStepResponse = [...stepTestResponses].reverse().find((body) => body?.trim())
      if (lastStepResponse) {
        try {
          const parsed = JSON.parse(lastStepResponse)
          extractVariablesFromObject(parsed, "response", addVariable)
        } catch (error) {
          console.warn("Failed to parse final chain step test response body as JSON:", error)
        }
      }
    } else if (testResponseBody) {
      try {
        const parsed = JSON.parse(testResponseBody)
        extractVariablesFromObject(parsed, "response", addVariable)
      } catch (error) {
        console.warn("Failed to parse test response body as JSON:", error)
      }
    }

    return variables
  }, [extractionSchema, testResponseBody, stepTestResponses])
}

function extractVariablesFromObject(
  obj: unknown,
  prefix: string,
  addVariable: (value: string) => void,
) {
  if (typeof obj === "object" && obj !== null) {
    if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        extractVariablesFromObject(item, `${prefix}[${index}]`, addVariable)
      })
    } else {
      for (const [key, value] of Object.entries(obj)) {
        const fullPath = prefix ? `${prefix}.${key}` : key
        if (typeof value === "object" && value !== null) {
          extractVariablesFromObject(value, fullPath, addVariable)
        } else {
          addVariable(fullPath)
        }
      }
    }
  }
}

export type { VariableOption }
